import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import GridLayout from 'react-grid-layout';
import {
    FaArrowRight, FaCheck, FaPlus, FaTimes,
} from 'react-icons/fa';
import { TbGripHorizontal } from 'react-icons/tb';
import moment from 'moment';
import { endOfDay } from 'date-fns';

import '../../../../../../node_modules/react-grid-layout/css/styles.css';

import { ReactComponent as IdlingAssetCarIcon } from '../../../../../assets/idling-asset-car-icon.svg';
import GreyHeader from '../../../../../shared/ui/GreyHeader';
import FilterMenu from '../../../component/FilterMenu';
import { CustomButtonToggle } from '../../../component/DropdownComponents';
import CustomButton from '../../../component/CustomButton';
import DashboardNotificationsBell from './DashboardNotificationsBell';
import FleetCameraHealthTile from './FleetCameraHealthTile';
import AddTileModal from './AddTileModal';
import DateDropdown from '../../../component/dump/menuComponents/DateDropdown';
import { getCompanyById } from '../../../../../shared/api/action/CompaniesActions';
import { getDevicesList } from '../../../../../shared/api/action/DeviceActions';
import { getUserNotifications } from '../../../../../shared/api/action/AccountActions';
import { getEventTypeCounts, getFleetIdlingDrivingSummary, getIdlingSummaryReport } from '../../../../../shared/api/action/ReportsActions';
import momenttz from '../../../component/helpers/momenttz';
import { detectDeviceStatus, getDeviceStatusName } from '../../../component/helpers/helperVehicle';
import { getAvailableFleetStatuses } from '../../../component/helpers/helperSleepMode';
import { getNotificationCategories } from '../../../component/helpers/helperNotificationCategories';
import { dvType } from '../../../component/helpers/constants';
import { slidersIcon } from '../../../component/helpers/iconHelpers';
import { formatVehicleName } from '../../../component/helpers/tablesFuncHelpers';
import { useIsMobile } from '../../../../../app/providers/MobileContext';

import '../../../component/FilterMenu.sass';
import './FleetManagerDashboard.sass';
import { getCustomDV7Access, getDV7Access, isDV7TestingVisible } from '../../../component/helpers/helperDV7Access';

const EVENT_TYPE_LABELS = {
    critical_gsensor: 'High_G-Force', speed: 'Max_Speed', driver: 'Driver', custom: 'Custom',
    harshaccel: 'Harsh_Acceleration', harshbraking: 'Harsh_Braking', adas_ldw: 'Lane_Departure',
    adas_fcw: 'Forward_Collision', adas_hw: 'Headway_Warning', adas_pcw: 'Pedestrian_Warning',
    tailgating: 'Tailgating', distraction: 'Distraction', drowsiness: 'Drowsiness/Head_Down',
    phone_use: 'Phone_Use/Hand_to_Head', yawning: 'Yawning', seatbelt: 'Seatbelt', smoking: 'Smoking',
    camerablock: 'Potential_Ch2_Obstruction', gsensor_wakeup: 'G-Sensor_Wake-Up',
    backup_warning: 'Dangerous_Backing-Up_Alerts', front_vehicle_start: 'Front_Vehicle_Start_Alert',
    trafficlightviolation_saw: 'Signal_Approach', trafficlightviolation_svw: 'Signal_Violation',
    stopsignviolation_rsw: 'Rolling_Stop', stopsignviolation_bsw: 'Bypass_Stop',
    blind_spot_warning_standard: 'Standard_Blind_Spot_Detection_Warning',
    blind_spot_warning_urban: 'Urban_Blind_Spot_Detection_Warning',
    low_speeding: 'Blind_Spot_Detection_Warning_Low', moderate_speeding: 'Blind_Spot_Detection_Warning_Moderate',
    high_speeding: 'Blind_Spot_Detection_Warning_High', severe_speeding: 'Blind_Spot_Detection_Warning_Severe',
    ituran: 'Ituran',
};
const EVENT_TYPE_API_KEYS = {
    critical_gsensor: ['FatalGSensor'],
    speed: ['HighSpeed'],
    driver: ['External Driver', 'InputSensor2', 'InputSensor3', 'InputSensor5', 'InputSensor9'],
    custom: ['FLEETMANAGER_CLIP', 'SYSTEMADMIN_CLIP', 'ENDUSER_CLIP'],
    distraction: ['Distraction', 'DmsWarning3'],
    drowsiness: ['Drowsiness', 'DmsWarning2'],
    phone_use: ['PhoneUse', 'DmsWarning4'],
    yawning: ['Yawning', 'DmsWarning5'],
    seatbelt: ['Seatbelt'],
    smoking: ['Smoking'],
    camerablock: ['CameraBlock', 'Camera_Block'],
    adas_hw: ['HeadwayWarning'],
    harshaccel: ['HarshAccel', 'harsh accel', 'harsh_accel'],
    harshbraking: ['HarshBraking', 'harsh_braking'],
    adas_ldw: ['LaneDeparture'],
    adas_fcw: ['ForwardCollisionWarning'],
    adas_pcw: ['PedestrianWarning'],
    tailgating: ['FollowingDistanceWarning'],
    gsensor_wakeup: ['GSensorWakeUp'],
    backup_warning: ['BackupWarning'],
    front_vehicle_start: ['FrontVehicleStart'],
    trafficlightviolation_saw: ['TrafficLightViolationSAW'],
    trafficlightviolation_svw: ['TrafficLightViolationSVW'],
    stopsignviolation_rsw: ['StopSignViolationRSW'],
    stopsignviolation_bsw: ['StopSignViolationBSW'],
    blind_spot_warning_standard: ['BlindSpotWarningStandard'],
    blind_spot_warning_urban: ['BlindSpotWarningUrban'],
    low_speeding: ['LowSpeeding', 'LSA'],
    moderate_speeding: ['ModerateSpeeding', 'MSA'],
    high_speeding: ['HighSpeeding', 'HSA'],
    severe_speeding: ['SevereSpeeding', 'SSA'],
    ituran: ['ITURAN_EVENT', 'Ituran'],
};
const STORAGE_KEY = 'dashboardFleetActivityStatusFilters';
const DEFAULT_STATUSES = { driving: true, sleep: true, offline: true, parked: true, idling: true, awake: true, disabled: true };
const STATUS_COLORS = {
    driving: '#3BB273', sleep: '#000000', offline: '#FFFFFF', parked: '#4B3F91',
    idling: '#F5A623', awake: '#4FC3F7', disabled: '#B0B0B0',
};
const EVENT_TYPE_CATEGORY = {
    critical_gsensor: 'safety', speed: 'safety', driver: 'safety', custom: 'safety',
    harshaccel: 'safety', harshbraking: 'safety', ituran: 'safety',
    adas_ldw: 'adas', adas_fcw: 'adas', adas_hw: 'adas', adas_pcw: 'adas', tailgating: 'adas',
    gsensor_wakeup: 'adas', backup_warning: 'adas', front_vehicle_start: 'adas',
    trafficlightviolation_saw: 'adas', trafficlightviolation_svw: 'adas',
    stopsignviolation_rsw: 'adas', stopsignviolation_bsw: 'adas',
    blind_spot_warning_standard: 'adas', blind_spot_warning_urban: 'adas',
    low_speeding: 'adas', moderate_speeding: 'adas', high_speeding: 'adas', severe_speeding: 'adas',
    distraction: 'dms', drowsiness: 'dms', phone_use: 'dms', yawning: 'dms',
    seatbelt: 'dms', smoking: 'dms', camerablock: 'dms',
};

const CARD_MIN_WIDTH = 340;
const CARD_MAX_WIDTH = 437;
const GRID_GAP_X = 20;
const GRID_GAP_Y = 30;
const GRID_ROW_HEIGHT = 598;
const MOBILE_GRID_ROW_HEIGHT = 491;
const ADD_TILE_KEY = 'add-tile';

const computeCols = (containerWidth) => {
    if (!containerWidth) return 1;
    let cols = Math.max(1, Math.floor((containerWidth + GRID_GAP_X) / (CARD_MIN_WIDTH + GRID_GAP_X)));
    for (;;) {
        const colWidth = (containerWidth - GRID_GAP_X * (cols - 1)) / cols;
        if (colWidth <= CARD_MAX_WIDTH) break;
        const nextColWidth = (containerWidth - GRID_GAP_X * cols) / (cols + 1);
        if (nextColWidth < CARD_MIN_WIDTH) break;
        cols += 1;
    }
    return cols;
};

const TILE_ORDER_STORAGE_KEY = 'dashboardTileOrder';
const DEFAULT_TILE_ORDER = ['tall', 'status', 'data-usage', 'idling-assets', 'idling', 'camera-health'];
const TILE_TITLE_KEYS = {
    tall: 'eventBreakdown.title',
    status: 'fleetActivityStatus.title',
    'data-usage': 'fleetDataUsage.title',
    'idling-assets': 'topIdlingAssets.title',
    idling: 'fleetIdling.title',
    'camera-health': 'fleetCameraHealth.title',
};

const formatIdlingTime = (value) => {
    const total = Math.round(Number(value) || 0);
    const seconds = total % 60;
    const hoursMinutes = moment.duration(total - seconds, 'seconds').format('h[h] m[m]', { trim: 'all', useGrouping: false });
    const showSeconds = seconds > 0 || !hoursMinutes;

    return (
        <span className="idling-assets-table__time">
            {hoursMinutes && <span className="idling-assets-table__time-part">{hoursMinutes}</span>}
            {hoursMinutes && showSeconds && ' '}
            {showSeconds && <span className="idling-assets-table__time-part">{seconds}s</span>}
        </span>
    );
};

const getSavedTileState = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(TILE_ORDER_STORAGE_KEY));
        const savedOrder = Array.isArray(saved?.order) ? saved.order : saved;
        if (!Array.isArray(savedOrder) || !savedOrder.length) return DEFAULT_TILE_ORDER;
        const knownTiles = Array.isArray(saved?.knownTiles) ? saved.knownTiles : savedOrder;
        const validOrder = savedOrder.filter((key) => DEFAULT_TILE_ORDER.includes(key));
        const newlyIntroduced = DEFAULT_TILE_ORDER.filter((key) => !knownTiles.includes(key) && !validOrder.includes(key));
        return [...validOrder, ...newlyIntroduced];
    } catch {
        return DEFAULT_TILE_ORDER;
    }
};

const buildLayout = (order, isCustomizing, cols) => {
    const keys = isCustomizing ? [...order, ADD_TILE_KEY] : order;
    return keys.map((key, index) => ({
        i: key,
        x: index % cols,
        y: Math.floor(index / cols),
        w: 1,
        h: 1,
        static: key === ADD_TILE_KEY,
    }));
};

const getDefaultFilters = (DV7access, customDV7Access) => ({
    critical_gsensor: true, speed: true, driver: true, custom: true, harshaccel: true,
    harshbraking: true, adas_fcw: true, adas_ldw: true, ...(!customDV7Access ? { adas_pcw: true } : {}), adas_hw: true,
    tailgating: true, distraction: true, drowsiness: true, phone_use: true, yawning: true,
    seatbelt: true, smoking: true, camerablock: true, feedback: true, reviewed: true,
    pending: true, ituran: true,
    ...(DV7access ? {
        ...(!customDV7Access ? {
            gsensor_wakeup: true, backup_warning: true, front_vehicle_start: true,
        } : {}),
        ...(isDV7TestingVisible(customDV7Access) ? { trafficlightviolation_saw: true, trafficlightviolation_svw: true } : {}),
        stopsignviolation_rsw: true, stopsignviolation_bsw: true,
        ...(!customDV7Access ? { blind_spot_warning_standard: true, blind_spot_warning_urban: true } : {}),
        low_speeding: true, moderate_speeding: true, high_speeding: true, severe_speeding: true,
    } : {}),
});

const getDefaultDateRange = () => ({
    start_date: moment().subtract(6, 'days').format('YYYYMMDD'),
    end_date: moment().format('YYYYMMDD'),
});

const makeDateRangeHandler = (setStartDate, setEndDate) => (start, end) => {
    if (start) setStartDate(moment(start).format('YYYYMMDD'));
    if (end) setEndDate(moment(end).format('YYYYMMDD'));
};

const BarListLabel = ({ itemKey, label }) => {
    const ref = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
    }, [label]);

    const content = (
        <span ref={ref} className="bar-list__label">
            {label}
        </span>
    );

    if (!isTruncated) return content;

    return (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`bar-list-tooltip-${itemKey}`}>{label}</Tooltip>}>
            {content}
        </OverlayTrigger>
    );
};

const FleetManagerDashboard = (props) => {
    const { toggleLoader } = props;
    const { t, i18n } = useTranslation('translation', { keyPrefix: 'dashboardPage' });
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const pendingRequestsRef = useRef(0);
    const unmountedRef = useRef(false);
    const latestRequestIdsRef = useRef({});
    const toggleRequestLoader = (show) => {
        if (unmountedRef.current) return;
        const prevCount = pendingRequestsRef.current;
        pendingRequestsRef.current = show ? prevCount + 1 : Math.max(prevCount - 1, 0);
        if (show && prevCount === 0) toggleLoader(true);
        if (!show && pendingRequestsRef.current === 0) toggleLoader(false);
    };
    // Returns a checker that is true only while this is the latest request for the key and the component is mounted
    const beginRequest = (key) => {
        const requestId = (latestRequestIdsRef.current[key] || 0) + 1;
        latestRequestIdsRef.current[key] = requestId;
        return () => !unmountedRef.current && latestRequestIdsRef.current[key] === requestId;
    };
    const user = useSelector((state) => state.user.user);
    const userRole = user.roles[0];
    const companyId = useSelector((state) => state.company.company.company_id);
    const company = useSelector((state) => state.company.company);
    const DV7access = getDV7Access(company);
    const customDV7Access = getCustomDV7Access(company);

    const defaultDateRange = getDefaultDateRange();
    const [eventStartDate, setEventStartDate] = useState(defaultDateRange.start_date);
    const [eventEndDate, setEventEndDate] = useState(defaultDateRange.end_date);
    const [counts, setCounts] = useState({});
    const [filters, setFilters] = useState(() => {
        const saved = JSON.parse(localStorage.getItem('eventsFilters'));
        return { ...getDefaultFilters(DV7access, customDV7Access), ...saved?.filters };
    });
    const [dataUsage, setDataUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [devices, setDevices] = useState([]);
    const [statusFilters, setStatusFilters] = useState(() => {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return { ...DEFAULT_STATUSES, ...saved };
    });
    const [notifications, setNotifications] = useState(null);
    const [idlingStartDate, setIdlingStartDate] = useState(defaultDateRange.start_date);
    const [idlingEndDate, setIdlingEndDate] = useState(defaultDateRange.end_date);
    const [idlingSummary, setIdlingSummary] = useState(null);
    const [assetIdlingStartDate, setAssetIdlingStartDate] = useState(defaultDateRange.start_date);
    const [assetIdlingEndDate, setAssetIdlingEndDate] = useState(defaultDateRange.end_date);
    const [topIdlingAssets, setTopIdlingAssets] = useState([]);
    const [isCustomizing, setIsCustomizing] = useState(false);
    const [tileOrder, setTileOrder] = useState(getSavedTileState);
    const [preCustomizeTileOrder, setPreCustomizeTileOrder] = useState(null);
    const [showAddTileModal, setShowAddTileModal] = useState(false);
    const [gridWidth, setGridWidth] = useState(0);
    const isMobile = useIsMobile();
    const gridRowHeight = isMobile ? MOBILE_GRID_ROW_HEIGHT : GRID_ROW_HEIGHT;
    const gridContainerRef = useRef(null);
    const cameraHealthApiRef = useRef(null);

    useEffect(() => () => {
        unmountedRef.current = true;
        if (pendingRequestsRef.current > 0) {
            pendingRequestsRef.current = 0;
            toggleLoader(false);
        }
    }, []);

    useEffect(() => {
        const el = gridContainerRef.current;
        if (!el) return undefined;
        let frameId;
        const updateWidth = () => {
            cancelAnimationFrame(frameId);
            frameId = requestAnimationFrame(() => setGridWidth(el.clientWidth));
        };
        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        observer.observe(el);
        return () => {
            cancelAnimationFrame(frameId);
            observer.disconnect();
        };
    }, []);

    const isTallVisible = tileOrder.includes('tall');
    const isStatusVisible = tileOrder.includes('status');
    const isDataUsageVisible = tileOrder.includes('data-usage');
    const isIdlingAssetsVisible = tileOrder.includes('idling-assets');
    const isIdlingVisible = tileOrder.includes('idling');
    const isCameraHealthVisible = tileOrder.includes('camera-health');

    const handleCustomizeClick = () => {
        setPreCustomizeTileOrder(tileOrder);
        setIsCustomizing(true);
    };

    const handleCancelCustomize = () => {
        if (preCustomizeTileOrder) setTileOrder(preCustomizeTileOrder);
        setIsCustomizing(false);
    };

    const handleSaveCustomize = () => {
        localStorage.setItem(TILE_ORDER_STORAGE_KEY, JSON.stringify({ order: tileOrder, knownTiles: DEFAULT_TILE_ORDER }));
        setIsCustomizing(false);
    };

    const handleRemoveTile = (key) => setTileOrder((prev) => prev.filter((tileKey) => tileKey !== key));

    const handleSaveAddTileModal = (newOrder) => {
        setTileOrder(newOrder);
        setShowAddTileModal(false);
    };

    const cols = useMemo(() => computeCols(gridWidth), [gridWidth]);
    const layout = useMemo(() => buildLayout(tileOrder, isCustomizing, cols), [tileOrder, isCustomizing, cols]);

    const handleDragStop = (draggedLayout, oldItem, newItem) => {
        if (!isCustomizing) return;
        const draggedKey = oldItem.i;
        const currentKeys = layout.map((item) => item.i);
        const draggedIndex = currentKeys.indexOf(draggedKey);
        if (draggedIndex === -1) return;

        const maxIndex = currentKeys.length - 1;
        const targetIndex = Math.min(Math.max(newItem.y * cols + newItem.x, 0), maxIndex);
        const targetKey = currentKeys[targetIndex];
        if (targetKey === draggedKey || targetKey === ADD_TILE_KEY) return;

        setTileOrder((prev) => {
            const next = [...prev];
            const from = next.indexOf(draggedKey);
            const to = next.indexOf(targetKey);
            if (from === -1 || to === -1) return prev;
            [next[from], next[to]] = [next[to], next[from]];
            return next;
        });
    };

    const renderCustomizeControls = (key) => (
        <>
            <span
                className="tile__drag-handle"
                role="button"
                tabIndex={0}
                aria-label={t('toolbar.ariaDragHandle')}
            >
                <TbGripHorizontal />
            </span>
            <button
                type="button"
                className="tile__remove-btn"
                onClick={() => handleRemoveTile(key)}
                aria-label={t('toolbar.ariaRemoveCard')}
            >
                <FaTimes />
            </button>
        </>
    );

    const { roadEventNotifications, dataPoolNotifications, fleetStatusNotifications } = getNotificationCategories(DV7access, customDV7Access);
    const idlingNotifications = fleetStatusNotifications.filter((notif) => notif.name === 'alert_idling');

    const fetchNotifications = () => {
        getUserNotifications(user.email, userRole)
            .then((res) => setNotifications(res.data.response.user.notifications))
            .catch(() => setNotifications(null));
    };

    const fetchEventCounts = () => {
        const isLatest = beginRequest('eventCounts');
        toggleRequestLoader(true);
        const start_ts = momenttz(moment(eventStartDate, 'YYYYMMDD').format('YYYY-MM-DD 00:00:00')).utc().format('YYYYMMDDHHmmss');
        const end_ts = momenttz(moment(eventEndDate, 'YYYYMMDD').format('YYYY-MM-DD 23:59:59')).utc().format('YYYYMMDDHHmmss');
        getEventTypeCounts({
            start_ts,
            end_ts,
            company_id: companyId,
            tzname: user.settings?.time_zone,
        })
            .then((res) => {
                if (!isLatest()) return;
                const report = res.data.response.report || {};
                const mappedCounts = Object.keys(EVENT_TYPE_API_KEYS).reduce((result, ourKey) => {
                    result[ourKey] = EVENT_TYPE_API_KEYS[ourKey].reduce((sum, apiKey) => sum + (report[apiKey] || 0), 0);
                    return result;
                }, {});
                setCounts(mappedCounts);
            })
            .catch(() => isLatest() && setCounts({}))
            .finally(() => toggleRequestLoader(false));
    };

    const fetchDataUsage = () => {
        const isLatest = beginRequest('dataUsage');
        setLoading(true);
        toggleRequestLoader(true);
        getCompanyById(userRole, companyId, 'data_usage')
            .then((res) => {
                if (!isLatest()) return;
                setDataUsage(res.data.response.company.data_usage);
                setLoading(false);
            })
            .catch(() => isLatest() && setLoading(false))
            .finally(() => toggleRequestLoader(false));
    };

    const fetchIdlingSummary = () => {
        const isLatest = beginRequest('idlingSummary');
        toggleRequestLoader(true);
        const start_ts = momenttz(moment(idlingStartDate, 'YYYYMMDD').format('YYYY-MM-DD 00:00:00')).utc().format('YYYYMMDDHHmmss');
        const end_ts = momenttz(moment(idlingEndDate, 'YYYYMMDD').format('YYYY-MM-DD 23:59:59')).utc().format('YYYYMMDDHHmmss');
        getFleetIdlingDrivingSummary(companyId, start_ts, end_ts)
            .then((res) => isLatest() && setIdlingSummary({
                idling_seconds: res.data.response.idling,
                driving_seconds: res.data.response.driving,
            }))
            .catch(() => isLatest() && setIdlingSummary(null))
            .finally(() => toggleRequestLoader(false));
    };

    const fetchTopIdlingAssets = () => {
        const isLatest = beginRequest('topIdlingAssets');
        toggleRequestLoader(true);
        getIdlingSummaryReport(companyId, assetIdlingStartDate, assetIdlingEndDate)
            .then((res) => {
                if (!isLatest()) return;
                const sorted = [...(res.data.response.report || [])].sort((a, b) => b.total_idling - a.total_idling);
                setTopIdlingAssets(sorted);
            })
            .catch(() => isLatest() && setTopIdlingAssets([]))
            .finally(() => toggleRequestLoader(false));
    };

    const fetchDevices = () => {
        const isLatest = beginRequest('devices');
        toggleRequestLoader(true);
        getDevicesList(companyId, 'inactive_devices,ignition')
            .then((res) => {
                if (!isLatest()) return;
                const arrDevices = DV7access ? res.data.response.devices : res.data.response.devices.filter((e) => dvType(e) !== 'dv7');
                setDevices(arrDevices.map((device) => ({ ...device, status: detectDeviceStatus(device) })));
            })
            .catch(() => isLatest() && setDevices([]))
            .finally(() => toggleRequestLoader(false));
    };

    useEffect(() => {
        fetchNotifications();
    }, [user.email, userRole]);

    useEffect(() => {
        if (!companyId || !isTallVisible) return;
        fetchEventCounts();
    }, [companyId, eventStartDate, eventEndDate, isTallVisible]);

    useEffect(() => {
        if (!companyId || !isDataUsageVisible) return;
        fetchDataUsage();
    }, [companyId, userRole, isDataUsageVisible]);

    useEffect(() => {
        if (!companyId || !isIdlingVisible) return;
        fetchIdlingSummary();
    }, [companyId, idlingStartDate, idlingEndDate, isIdlingVisible]);

    useEffect(() => {
        if (!companyId || !isIdlingAssetsVisible) return;
        fetchTopIdlingAssets();
    }, [companyId, assetIdlingStartDate, assetIdlingEndDate, isIdlingAssetsVisible]);

    useEffect(() => {
        if (!companyId || (!isStatusVisible && !isTallVisible)) return;
        fetchDevices();
    }, [companyId, DV7access, isStatusVisible, isTallVisible]);

    const loadAllData = () => {
        fetchNotifications();
        if (!companyId) return;
        if (isTallVisible) fetchEventCounts();
        if (isDataUsageVisible) fetchDataUsage();
        if (isIdlingVisible) fetchIdlingSummary();
        if (isIdlingAssetsVisible) fetchTopIdlingAssets();
        if (isStatusVisible || isTallVisible) fetchDevices();
        if (isCameraHealthVisible) cameraHealthApiRef.current?.reload();
    };

    const handleRefreshClick = () => loadAllData();

    const persistEventFilters = (newFilters) => {
        const saved = JSON.parse(localStorage.getItem('eventsFilters')) || {};
        localStorage.setItem('eventsFilters', JSON.stringify({ ...saved, filters: newFilters }));
        setFilters(newFilters);
    };

    const selectAllEventFilters = () => {
        const allSelected = Object.values(filters).every(Boolean);
        const newFilters = Object.keys(filters).reduce((result, key) => {
            result[key] = !allSelected;
            return result;
        }, {});
        persistEventFilters(newFilters);
    };

    const availableFilters = getDefaultFilters(DV7access, customDV7Access);
    const eventItems = useMemo(() => Object.keys(EVENT_TYPE_LABELS)
        .filter((key) => key in availableFilters && filters[key] !== false && counts[key] > 0)
        .map((key) => ({
            key,
            label: i18n.t(`constants.eventNames.${EVENT_TYPE_LABELS[key]}`),
            subLabel: t(`eventBreakdown.categories.${EVENT_TYPE_CATEGORY[key]}`),
            value: counts[key],
        }))
        .sort((a, b) => b.value - a.value), [filters, counts, i18n]);

    const idlingHours = (idlingSummary?.idling_seconds || 0) / 3600;
    const drivingHours = (idlingSummary?.driving_seconds || 0) / 3600;
    const totalActivityHours = idlingHours + drivingHours;
    const idlePercentage = totalActivityHours ? Math.min((idlingHours * 100) / totalActivityHours, 100) : 0;

    const totalEvents = useMemo(() => eventItems.reduce((sum, item) => sum + item.value, 0), [eventItems]);
    const averagePerVehicle = devices.length ? Math.round(totalEvents / devices.length) : 0;

    const goToZonesReport = (payload) => {
        dispatch({ type: 'SET_ZONES_REPORT', payload });
        navigate('/reports');
    };

    const goToIdlingSummary = () => goToZonesReport({ reportId: '14', start_date: idlingStartDate, end_date: idlingEndDate });

    const goToIdlingBreakdown = (assetName) => goToZonesReport({
        reportId: '15', searchTerm: assetName, start_date: assetIdlingStartDate, end_date: assetIdlingEndDate,
    });

    const goToIdlingBreakdownAll = () => goToZonesReport({ reportId: '15', start_date: assetIdlingStartDate, end_date: assetIdlingEndDate });

    const availableGB = (dataUsage?.available_data_pool || 0) / 1024 / 1024 / 1024;
    const usedGB = (dataUsage?.total_data_usage || 0) / 1024 / 1024 / 1024;
    const remainingGB = Math.max(availableGB - usedGB, 0);
    const usedPercentage = availableGB ? Math.min((usedGB * 100) / availableGB, 100) : 0;

    const toggleStatus = (status) => {
        const newFilters = { ...statusFilters, [status]: !statusFilters[status] };
        setStatusFilters(newFilters);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newFilters));
    };

    const selectAllStatuses = () => {
        const allSelected = Object.values(statusFilters).every(Boolean);
        const newFilters = Object.keys(statusFilters).reduce((result, key) => {
            result[key] = !allSelected;
            return result;
        }, {});
        setStatusFilters(newFilters);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newFilters));
    };

    const statusCounts = devices.reduce((result, device) => {
        result[device.status] = (result[device.status] || 0) + 1;
        return result;
    }, Object.keys(DEFAULT_STATUSES).reduce((result, key) => {
        result[key] = 0;
        return result;
    }, {}));

    const availableStatuses = getAvailableFleetStatuses(statusCounts, 'glance');
    const availableStatusFilters = getAvailableFleetStatuses(statusFilters, 'glance');
    const statusItems = Object.keys(availableStatuses)
        .filter((status) => statusFilters[status])
        .map((status) => {
            const value = availableStatuses[status];
            const statusName = i18n.t(`homePage.fleetStatuses.${getDeviceStatusName(status).replace(' ', '_')}`, getDeviceStatusName(status));
            return { key: status, label: `${statusName} - ${value}`, value, dotColor: STATUS_COLORS[status] };
        })
        .sort((a, b) => b.value - a.value);

    const renderBarList = (items, emptyLabel, { hideValue, withTruncationTooltip, showIcon } = {}) => {
        if (!items.length) return <div className="bar-list__empty">{emptyLabel}</div>;
        const maxValue = Math.max(...items.map(({ value }) => value), 1);
        return (
            <ul className="bar-list">
                {items.map(({ key, label, subLabel, dotColor, value }) => (
                    <li key={key} className="bar-list__row">
                        {showIcon && <span className="bar-list__icon"><img src="/images/caution.png" alt="" /></span>}
                        {dotColor && <span className={`bar-list__dot bar-list__dot--${key}`} style={{ background: dotColor }} />}
                        <span className="bar-list__labelcol">
                            {withTruncationTooltip
                                ? <BarListLabel itemKey={key} label={label} />
                                : <span className="bar-list__label" title={label}>{label}</span>}
                            {subLabel && <span className="bar-list__sublabel">{subLabel}</span>}
                        </span>
                        <span className="bar-list__track">
                            <span className="bar-list__fill" style={{ width: `${(value / maxValue) * 100}%` }} />
                        </span>
                        {!hideValue && <span className="bar-list__value">{value}</span>}
                    </li>
                ))}
            </ul>
        );
    };

    const addTileOptions = DEFAULT_TILE_ORDER.map((key) => ({ key, label: t(TILE_TITLE_KEYS[key]) }));

    return (
        <div className="fm-home">
            <GreyHeader title={t('title')} />
            <div className="fm-home__toolbar">
                {isCustomizing ? (
                    <>
                        <CustomButton variant="secondary-outline" onClick={handleCancelCustomize}>
                            {t('toolbar.btnCancel')}
                        </CustomButton>
                        <CustomButton variant="primary" size="sm" onClick={handleSaveCustomize}>
                            {t('toolbar.btnSave')}
                        </CustomButton>
                    </>
                ) : (
                    <>
                        <CustomButton variant="primary-outline" onClick={handleCustomizeClick}>
                            {t('toolbar.btnCustomize')}
                        </CustomButton>
                        <CustomButton variant="primary-outline" onClick={handleRefreshClick}>
                            {t('toolbar.btnRefresh')}
                        </CustomButton>
                    </>
                )}
            </div>
            {showAddTileModal && (
                <AddTileModal
                    onHide={() => setShowAddTileModal(false)}
                    onSave={handleSaveAddTileModal}
                    tiles={addTileOptions}
                    tileOrder={tileOrder}
                />
            )}
            <div className="fm-home__tiles">
                <div
                    className="fm-home__tiles-inner"
                    ref={gridContainerRef}
                    style={{ '--grid-row-height': `${gridRowHeight}px` }}
                >
                    {gridWidth > 0 && (
                        <GridLayout
                            width={gridWidth}
                            cols={cols}
                            rowHeight={gridRowHeight}
                            margin={[GRID_GAP_X, GRID_GAP_Y]}
                            containerPadding={[0, 0]}
                            layout={layout}
                            compactType={null}
                            onDragStop={handleDragStop}
                            isDraggable={isCustomizing}
                            isResizable={false}
                            draggableHandle=".tile__drag-handle"
                        >
                            {isTallVisible && (
                                <div key="tall" className="tile tile--tall">
                                    {isCustomizing && renderCustomizeControls('tall')}
                                    <div className="tile__hd">
                                        <span className="tile__title">{t('eventBreakdown.title')}</span>
                                        <div className="tile__hd-right">
                                            <DateDropdown
                                                start_date={eventStartDate}
                                                end_date={eventEndDate}
                                                onSelectDate={makeDateRangeHandler(setEventStartDate, setEventEndDate)}
                                                maxDate={endOfDay(new Date())}
                                            />
                                        </div>
                                    </div>
                                    <div className="tile__bd">
                                        <div className="tile__stats">
                                            <div className="tile__stat-box" data-testid="event-breakdown-total">
                                                <span className="tile__stat-value">{totalEvents}</span>
                                                <span className="tile__stat-label">{t('eventBreakdown.textTotalEvents')}</span>
                                            </div>
                                            <div className="tile__stat-box" data-testid="event-breakdown-average">
                                                <span className="tile__stat-value">{averagePerVehicle}</span>
                                                <span className="tile__stat-label">{t('eventBreakdown.textAveragePerVehicle')}</span>
                                            </div>
                                        </div>
                                        {renderBarList(eventItems, t('eventBreakdown.textNoEvents'), { withTruncationTooltip: true, showIcon: true })}
                                    </div>
                                    <div className="tile__ft">
                                        {Object.values(filters).some(Boolean) && eventItems.length > 0 &&
                                            <button type="button" className="tile__btn" onClick={() => {
                                                navigate('/event', {
                                                    state: {
                                                        type: 'redirectFromDashboardEventBreakdown',
                                                        start_date: moment(eventStartDate, 'YYYYMMDD').format('YYYY-MM-DD'),
                                                        end_date: moment(eventEndDate, 'YYYYMMDD').format('YYYY-MM-DD'),
                                                    },
                                                });
                                            }}>
                                                <span className="tile__btn-label">{t('eventBreakdown.btnViewEvents')}</span>
                                                <span className="tile__btn-icon"><FaArrowRight size={18} /></span>
                                            </button>
                                        }
                                        <div className="tile__ft-right">
                                            <DashboardNotificationsBell
                                                panelTitle={t('eventBreakdown.btnNotifications')}
                                                subcategories={roadEventNotifications.subcategories}
                                                notifications={notifications}
                                                userEmail={user.email}
                                                userRole={userRole}
                                                getNotifications={fetchNotifications}
                                                scrollable
                                                twoColumns
                                            />
                                            <FilterMenu
                                                userRole={userRole}
                                                user={user}
                                                filters={filters}
                                                fillFilters={(key) => persistEventFilters({ ...filters, [key]: !filters[key] })}
                                                selectAllFilters={selectAllEventFilters}
                                                DV7access={DV7access}
                                                customDV7Access={customDV7Access}
                                                toggleIcon={slidersIcon}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isStatusVisible && (
                                <div key="status" className="tile tile--status">
                                    {isCustomizing && renderCustomizeControls('status')}
                                    <div className="tile__hd">
                                        <span className="tile__title">{t('fleetActivityStatus.title')}</span>
                                    </div>
                                    <div className="tile__bd">
                                        <div className="tile__hero-total">
                                            {t('fleetActivityStatus.textTotalAssets')}
                                            <span className="tile__hero-total-val">{devices.length}</span>
                                        </div>
                                        {renderBarList(statusItems, t('fleetActivityStatus.textNoStatusesSelected'), { hideValue: true })}
                                    </div>
                                    <div className="tile__ft">
                                        {Object.values(statusFilters).some(Boolean) &&
                                            <button type="button" className="tile__btn" onClick={() => {
                                                dispatch({
                                                    type: 'SET_DASHBOARD_REDIRECT',
                                                    payload: { type: 'redirectFromFleetActivityStatus', statuses: statusFilters },
                                                });
                                                navigate('/');
                                            }}>
                                                <span className="tile__btn-label">{t('fleetActivityStatus.btnMapView')}</span>
                                                <span className="tile__btn-icon"><FaArrowRight size={18} /></span>
                                            </button>
                                        }
                                        <div className="tile__ft-right">
                                            <Dropdown align="end">
                                                <Dropdown.Toggle as={CustomButtonToggle} variant="primary-outline" wrapperClassName="no-caret filter-btn">
                                                    {slidersIcon}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu className="event-filter-menu typeFilterMenu">
                                                    <h5 className="category-title">{t('fleetActivityStatus.filterTitle')}</h5>
                                                    <CustomButton variant="link" onClick={selectAllStatuses}>
                                                        {`${Object.values(statusFilters).every(Boolean) ? i18n.t('eventsPage.FilterMenu.unselect') : i18n.t('eventsPage.FilterMenu.select')} ${i18n.t('eventsPage.FilterMenu.all')}`}
                                                    </CustomButton>
                                                    <ul className="filters-list">
                                                        {Object.keys(availableStatusFilters).map((status) => (
                                                            <li key={status} title={i18n.t(`homePage.fleetStatuses.${status.replace(' ', '_')}`, status)} onClick={() => toggleStatus(status)}>
                                                                <span className="name">{i18n.t(`homePage.fleetStatuses.${status.replace(' ', '_')}`, status)}</span>
                                                                {statusFilters[status] && <FaCheck className="flagSelected" />}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isDataUsageVisible && (
                                <div key="data-usage" className="tile tile--data-usage">
                                    {isCustomizing && renderCustomizeControls('data-usage')}
                                    <div className="tile__hd">
                                        <span className="tile__title">{t('fleetDataUsage.title')}</span>
                                    </div>
                                    <div className="tile__bd">
                                        {!loading && dataUsage && (
                                            <div className="data-usage">
                                                <div className="data-usage__donut" style={{ '--used-deg': `${usedPercentage * 3.6}deg` }}>
                                                    <div className="data-usage__donut-in">
                                                        <div className="data-usage__pct">{`${usedPercentage.toFixed(1)}%`}</div>
                                                        <div className="data-usage__pct-label">{t('fleetDataUsage.textUsed')}</div>
                                                    </div>
                                                </div>
                                                <div className="data-usage__legend">
                                                    <div className="data-usage__legend-row">
                                                        <span className="data-usage__dot used" />
                                                        <span className="data-usage__legend-label">{t('fleetDataUsage.textDataUsed')}</span>
                                                        <span className="data-usage__legend-value">{`${usedGB.toFixed(1)}GB`}</span>
                                                    </div>
                                                    <div className="data-usage__legend-row">
                                                        <span className="data-usage__dot remaining" />
                                                        <span className="data-usage__legend-label">{t('fleetDataUsage.textDataRemaining')}</span>
                                                        <span className="data-usage__legend-value">{`${remainingGB.toFixed(1)}GB`}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div className="tile__total">
                                            <span className="tile__total-label">{t('fleetDataUsage.textTotalDataPool')}</span>
                                            <span className="tile__total-val">{`${availableGB.toFixed(1)}GB`}</span>
                                        </div>
                                    </div>
                                    <div className="tile__ft">
                                        <button type="button" className="tile__btn" onClick={() => navigate('/managevehicles')}>
                                            <span className="tile__btn-label">{t('fleetDataUsage.btnGoToAssets')}</span>
                                            <span className="tile__btn-icon"><FaArrowRight size={18} /></span>
                                        </button>
                                        <div className="tile__ft-right">
                                            <DashboardNotificationsBell
                                                panelTitle={t('fleetDataUsage.btnNotifications')}
                                                subcategories={[{ notifsArr: dataPoolNotifications }]}
                                                notifications={notifications}
                                                userEmail={user.email}
                                                userRole={userRole}
                                                getNotifications={fetchNotifications}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isIdlingAssetsVisible && (
                                <div key="idling-assets" className="tile tile--idling-assets" data-testid="top-idling-assets-tile">
                                    {isCustomizing && renderCustomizeControls('idling-assets')}
                                    <div className="tile__hd">
                                        <span className="tile__title">{t('topIdlingAssets.title')}</span>
                                        <div className="tile__hd-right">
                                            <DateDropdown
                                                start_date={assetIdlingStartDate}
                                                end_date={assetIdlingEndDate}
                                                onSelectDate={makeDateRangeHandler(setAssetIdlingStartDate, setAssetIdlingEndDate)}
                                                maxDate={endOfDay(new Date())}
                                            />
                                        </div>
                                    </div>
                                    <div className="tile__bd">
                                        {topIdlingAssets.length
                                            ? (
                                                <table className="idling-assets-table">
                                                    <thead>
                                                        <tr>
                                                            <th>{t('topIdlingAssets.colAsset')}</th>
                                                            <th>{t('topIdlingAssets.colTotal')}</th>
                                                            <th>{t('topIdlingAssets.colAverage')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {topIdlingAssets.map((asset) => {
                                                            const assetName = formatVehicleName(asset.device_id, asset);
                                                            return (
                                                                <tr key={asset.device_id} data-testid="top-idling-assets-row">
                                                                    <td>
                                                                        <span className="idling-assets-table__asset-icon"><IdlingAssetCarIcon /></span>
                                                                        <button
                                                                            type="button"
                                                                            className="idling-assets-table__asset-btn"
                                                                            title={assetName}
                                                                            aria-label={`${t('topIdlingAssets.ariaViewAsset')} ${assetName}`}
                                                                            onClick={() => goToIdlingBreakdown(assetName)}
                                                                        >
                                                                            {assetName}
                                                                        </button>
                                                                    </td>
                                                                    <td>{formatIdlingTime(asset.total_idling)}</td>
                                                                    <td>{formatIdlingTime(asset.idling_average)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            )
                                            : <div className="bar-list__empty">{t('topIdlingAssets.textNoData')}</div>
                                        }
                                    </div>
                                    <div className="tile__ft">
                                        <button type="button" className="tile__btn" onClick={goToIdlingBreakdownAll} data-testid="top-idling-assets-see-all">
                                            <span className="tile__btn-label">{t('topIdlingAssets.btnSeeAll')}</span>
                                            <span className="tile__btn-icon"><FaArrowRight size={18} /></span>
                                        </button>
                                        <div className="tile__ft-right">
                                            <DashboardNotificationsBell
                                                panelTitle={t('topIdlingAssets.btnNotifications')}
                                                subcategories={[{ notifsArr: idlingNotifications }]}
                                                notifications={notifications}
                                                userEmail={user.email}
                                                userRole={userRole}
                                                getNotifications={fetchNotifications}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isIdlingVisible && (
                                <div key="idling" className="tile tile--idling">
                                    {isCustomizing && renderCustomizeControls('idling')}
                                    <div className="tile__hd">
                                        <span className="tile__title">{t('fleetIdling.title')}</span>
                                        <div className="tile__hd-right">
                                            <DateDropdown
                                                start_date={idlingStartDate}
                                                end_date={idlingEndDate}
                                                onSelectDate={makeDateRangeHandler(setIdlingStartDate, setIdlingEndDate)}
                                                maxDate={endOfDay(new Date())}
                                            />
                                        </div>
                                    </div>
                                    <div className="tile__bd">
                                        {idlingSummary && (
                                            <div className="data-usage">
                                                <div className="data-usage__donut" style={{ '--used-deg': `${idlePercentage * 3.6}deg` }}>
                                                    <div className="data-usage__donut-in">
                                                        <div className="data-usage__pct">{`${idlePercentage.toFixed(0)}%`}</div>
                                                        <div className="data-usage__pct-label">{t('fleetIdling.textIdle')}</div>
                                                    </div>
                                                </div>
                                                <div className="data-usage__legend">
                                                    <div className="data-usage__legend-row">
                                                        <span className="data-usage__dot used" />
                                                        <span className="data-usage__legend-label">{t('fleetIdling.textIdle')}</span>
                                                        <span className="data-usage__legend-value">{`${idlingHours.toFixed(1)}hr`}</span>
                                                    </div>
                                                    <div className="data-usage__legend-row">
                                                        <span className="data-usage__dot remaining" />
                                                        <span className="data-usage__legend-label">{t('fleetIdling.textDriving')}</span>
                                                        <span className="data-usage__legend-value">{`${drivingHours.toFixed(1)}hr`}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div className="tile__total">
                                            <span className="tile__total-label">{t('fleetIdling.textTotalFleetActivity')}</span>
                                            <span className="tile__total-val">{`${totalActivityHours.toFixed(1)}hr`}</span>
                                        </div>
                                    </div>
                                    <div className="tile__ft">
                                        <button type="button" className="tile__btn" onClick={goToIdlingSummary}>
                                            <span className="tile__btn-label">{t('fleetIdling.btnIdleSummary')}</span>
                                            <span className="tile__btn-icon"><FaArrowRight size={18} /></span>
                                        </button>
                                        <div className="tile__ft-right">
                                            <DashboardNotificationsBell
                                                panelTitle={t('fleetIdling.btnNotifications')}
                                                subcategories={[{ notifsArr: idlingNotifications }]}
                                                notifications={notifications}
                                                userEmail={user.email}
                                                userRole={userRole}
                                                getNotifications={fetchNotifications}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isCameraHealthVisible && (
                                <FleetCameraHealthTile
                                    key="camera-health"
                                    companyId={companyId}
                                    apiRef={cameraHealthApiRef}
                                    customizeControls={isCustomizing && renderCustomizeControls('camera-health')}
                                />
                            )}

                            {isCustomizing && (
                                <div key={ADD_TILE_KEY} className="tile tile--add">
                                    <button
                                        type="button"
                                        className="tile--add__btn"
                                        onClick={() => setShowAddTileModal(true)}
                                        aria-label={t('toolbar.btnAddTile')}
                                    >
                                        <FaPlus />
                                    </button>
                                    <span className="tile--add__label">{t('toolbar.btnAddTile')}</span>
                                </div>
                            )}
                        </GridLayout>
                    )}
                </div>
            </div>
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    toggleLoader: (show) => {
        dispatch({ type: 'TOGGLE_LOADER', payload: show });
    },
});

export default connect(null, mapDispatchToProps)(FleetManagerDashboard);
